import { Injectable } from '@angular/core';
import { IMqttMessage } from 'ngx-mqtt';
import { auditTime, BehaviorSubject, map, Observable, of, shareReplay, Subject } from 'rxjs';
import { Controller } from 'src/app/core/models/controller.model';
import { ProjectObject } from 'src/app/core/models/hvac-modes/project-object.model';
import { ProjectSubObject } from 'src/app/core/models/hvac-modes/project-sub-object.model';
import { Location } from 'src/app/core/models/project/location.model';
import { Property } from 'src/app/core/models/project/property.model';
import { LoadingService } from 'src/app/shared/services/loading.service';

export interface StoredObject {
  userId: number;
  object: string;
}

@Injectable({
  providedIn: 'root',
})
export class ProjectService {

  private objects$ = new BehaviorSubject<ProjectObject[]>([]);
  private subObjects$ = new BehaviorSubject<ProjectSubObject[]>([]);


  // private filteredControllersFat$ = new BehaviorSubject<Controller[]>([])
  private loadingFilteredControllersFat$ = new BehaviorSubject<boolean>(false)
  private allControllersThin$ = new BehaviorSubject<Controller[]>([])
  private rawUpdatedPipe$ = new Subject<Date>();
  public updatedPipe$ = this.rawUpdatedPipe$.pipe(
    auditTime(500), // Only emit once every 500ms, emits last value
    shareReplay(1)     // Share the last emission with new subscribers
  );
  // doorsChanged = new Subject<[string, string]>();
  savedDateFromChanged = new Subject<Date>();
  savedDateToChanged = new Subject<Date>();

  selectedobjectLocalStorageKey = 'irooms_selected_object';
  loadedObjectForUserId: number;

  private notPolledControllers: Array<Controller> = [];
  notPolledControllersChanged = new Subject<Controller[]>();

  allFatLocationsReady$ = new BehaviorSubject<boolean>(false);
  singleControllerMessage$ = new Subject<IMqttMessage>();

  private filteredGuestRoomLocations$ = new BehaviorSubject<Location[]>([]);

  // used to trigger and show loading spinner for room overview with over 50 locations
  public guestRoomLocationsCachedAndSizeReduced$ = new BehaviorSubject<boolean>(false);

  private filteredCommonAreasLocations$ = new BehaviorSubject<Location[]>([]);
  private allLocations$ = new BehaviorSubject<Location[]>([]);
  cardSyncActive$ = new BehaviorSubject<boolean>(false);

  latestCardFromKey = 'latestCardFromKey';
  latestCardToKey = 'latestCardToKey';

  constructor(private loadingService: LoadingService) {}

  // NEW REQUESTS

  getFilteredGuestRoomLocations() {
    return this.filteredGuestRoomLocations$.asObservable();
  }

  setFilteredGuestRoomLocations(locations: Location[]) {
    this.filteredGuestRoomLocations$.next(locations);
  }

  getFilteredCommonAreaLocations() {
    return this.filteredCommonAreasLocations$.asObservable();
  }

  setFilteredCommonAreasLocations(locations: Location[]) {
    this.filteredCommonAreasLocations$.next(locations);
  }

  getAllLocations() {
    return this.allLocations$.asObservable();
  }

  setAllLocations(locations: Location[], fatReady?: boolean) {
    if (fatReady) {
      this.allFatLocationsReady$.next(true);
    }
    this.allLocations$.next(locations)
  }

  getFilteredGuestRoomLocationById(locationId: string) {
    return this.filteredGuestRoomLocations$.getValue().find( loc => loc.locationId === +locationId)
  }

  private getFilteredCommonAreaLocationById(locationId: string) {
    return this.filteredCommonAreasLocations$.getValue().find( loc => loc.locationId === +locationId)
  }

  getLocationById(locationId: string) {
    return this.allLocations$.getValue().find( loc => loc.locationId === +locationId)
  }

  getGuestRoomLocations() {
    return this.allLocations$.getValue().filter( location => Location.isGuestRoom(location));
  }

  getAccessControlLocations$() {
    return of(this.allLocations$.getValue().filter( location => Location.isAccessControl(location) ||  Location.isPassageRelay(location)));
  }
  // NEW REQUESTS


  /* setFilteredControllersFat(controllers: Controller[]) {
    this.filteredControllersFat$.next(controllers);
  } */

  getLoadingFilteredLocations() {
    return this.loadingFilteredControllersFat$.asObservable();
  }


  setLoadingFilteredLocations(loading: boolean) {
    this.loadingFilteredControllersFat$.next(loading);
  }




  getRoom2Controllers$() {
    const locations = this.allLocations$.getValue().filter( location => Location.isGuestRoom(location))
    const controllers = locations.reduce( (roomList, location) => {
        roomList.push(...location.controllers.$values);
      return roomList
    }, [])
    return of(controllers)
  }

  getHvacControllers$() {
    const locations = this.allLocations$.getValue().filter( location => Location.isHvacCommonArea(location))
    const controllers = locations.reduce( (roomList, location) => {
        roomList.push(...location.controllers.$values);
      return roomList
    }, [])
    return of(controllers)
  }

  getLightControllers$() {
    const locations = this.allLocations$.getValue().filter( location => Location.isIOCommonArea(location))
    const controllers = locations.reduce( (roomList, location) => {
        roomList.push(...location.controllers.$values);
      return roomList
    }, [])
    return of(controllers)
  }

  getAccessControllControllers$() {
    const locations = this.allLocations$.getValue().filter( location => Location.isAccessControl(location))
    const controllers = locations.reduce( (roomList, location) => {
        roomList.push(...location.controllers.$values);
      return roomList
    }, [])
    return of(controllers)
  }

  getAllControllersFat(): Observable<Controller[]> {
    return this.allLocations$.pipe(
      map((allLocations)=> {
        return  allLocations.reduce( (roomList: Controller[], location) => {
        roomList.push(...location.controllers.$values);
        return roomList
          }, [])
    }))

   /*  const locations = this.allLocations$.getValue();
    console.log(locations)
    const controllers = locations.reduce( (roomList: Controller[], location) => {
        roomList.push(...location.controllers.$values);
          return roomList
        }, [])
  return of(controllers) */
  }

  getDriversList(): Observable<string[]> {
    return this.getAllControllersFat().pipe(
      map((allControllers)=> {
        return  allControllers.reduce( (driverList: string[], controller: Controller) => {
          if (!driverList.includes(controller.driverPrefix)) {
            driverList.push(controller.driverPrefix)
          }
        return driverList
          }, [])
    }))
  }


  getAllControllersThin() {
    return this.allControllersThin$.asObservable();
  }


  setObjects(objects: ProjectObject[]){
    this.objects$.next(objects)
  }

  getObjects(): Observable<ProjectObject[]> {
    return this.objects$.asObservable();
  }

  setSubObjects(subObjects: ProjectSubObject[]){
    this.subObjects$.next(subObjects)
  }

  getSubObjects() {
    return this.subObjects$.asObservable();
  }



  getControllerByLocationId(locId: string) {
  let targetCont: Controller
   this.allLocations$.getValue().find( location => { location.controllers.$values.find( cont => {
    if (cont.locationId == +locId) {
      targetCont = cont
    }
   })})
   if (targetCont) {
    return targetCont
   }
  }

  getThinLocationByLocationName(locationName: string) {
    return this.allLocations$.getValue().find( loc => {
      return loc.name.toLowerCase() === locationName.toString().toLowerCase()
    })
  }

  private getControllerByDesignation(designation: string) {
    let targetCont: Controller;
    let filteredLocations = [];
    const onCommonAreasPage =  window.document.URL.toString().includes('common-area');
    const onRoomOverviewPage = window.document.URL.toString().includes('rooms')

    if (onCommonAreasPage) {
      filteredLocations = this.filteredCommonAreasLocations$.getValue();
    }
    if (onRoomOverviewPage) {
      filteredLocations = this.filteredGuestRoomLocations$.getValue();
    }

    filteredLocations.some( location => {
      targetCont = location.controllers.$values.find((cont: Controller)=> {
          return cont.designation === designation;
        })
        return targetCont
    })
    return targetCont
  }

  private getControllerByDesignationFromFull(designation: string) {
    let targetCont: Controller;
    this.allLocations$.getValue().some( location => {
      targetCont = location.controllers.$values.find((cont: Controller)=> {
          return cont.designation === designation;
        })
        return targetCont
    })
    return targetCont
  }


  findPropByType(controller: Controller, propDown: number, propUp: number) {
    return controller.controllerProperties.$values.find( prop => {
      if (prop.type >= propDown && prop.type <= propUp) {
        return true;
      }
    })
  }

  updateFilteredProjectByMqtt(message: IMqttMessage) {
    const onCommonAreasPage =  window.document.URL.toString().includes('/common-area');
    const onRoomOverviewPage = window.document.URL.toString().includes('/rooms');

    let filteredLocations = [];
    if (onCommonAreasPage) {
      filteredLocations = this.filteredCommonAreasLocations$.getValue();
    }
    else if (onRoomOverviewPage) {
      filteredLocations = this.filteredGuestRoomLocations$.getValue();
    }

    if (filteredLocations.length === 0) {
      return;
    }
    const payloadIndex = message.payload.toString().lastIndexOf('|');
    const payload = message.payload.toString().slice(payloadIndex + 1);

    // example topic cli/driver/1/0/0/101/0/hvac/temp/set/room
    const driverPref = message.topic.split('/')[1] + '/' + message.topic.split('/')[2];
    const obj = message.topic.split('/')[3];
    const subObj = message.topic.split('/')[4];
    const zoneId = message.topic.split('/')[5];
    const subZoneId = message.topic.split('/')[6];
    const propTopic = this.getPropTopic(message.topic);
    let targetProp: Property;
    let targetCont: Controller;

    if (propTopic) {
      filteredLocations.find( location => {
        return location.controllers.$values.some( cont => {
          if (cont.driverPrefix.toLowerCase() === driverPref.toLowerCase() && cont.object === obj && cont.subObject === subObj && cont.zone === zoneId && cont.subZone === subZoneId) {
            targetCont = cont;
            targetProp = cont.controllerProperties.$values.find((prop)=> {
              return prop.mqttTopic === propTopic
            })
            return true;
          }
        })
      })
    }
    if (targetProp) {
      this.loadingService.removeFromLoading(`${targetCont.id}-${targetProp.id}`)
      targetProp.value = payload;
      if (onCommonAreasPage) {
        this.setFilteredCommonAreasLocations(filteredLocations);
      }
      if (onRoomOverviewPage) {
        this.setFilteredGuestRoomLocations(filteredLocations);
      }
      if (onCommonAreasPage || onRoomOverviewPage) {
        this.updatePipe();
      }
    }
  }

  updateFullProjectByMqtt(message: IMqttMessage) {
    const allLocations = this.allLocations$.getValue();
    if (allLocations.length === 0) {
      return;
    }
    const payloadIndex = message.payload.toString().lastIndexOf('|');
    const payload = message.payload.toString().slice(payloadIndex + 1);

    // example topic cli/driver/1/0/0/101/0/hvac/temp/set/room
    const driverPref = message.topic.split('/')[1] + '/' + message.topic.split('/')[2];
    const obj = message.topic.split('/')[3];
    const subObj = message.topic.split('/')[4];
    const zoneId = message.topic.split('/')[5];
    const subZoneId = message.topic.split('/')[6];
    const propTopic = this.getPropTopic(message.topic);
    let targetProp: Property;

    if (propTopic) {
      allLocations.find( location => {
        return location.controllers.$values.some( cont => {
          if (cont.driverPrefix.toLowerCase() === driverPref.toLowerCase() && cont.object === obj && cont.subObject === subObj && cont.zone === zoneId && cont.subZone === subZoneId) {
            targetProp = cont.controllerProperties.$values.find((prop)=> {
              return prop.mqttTopic === propTopic
            })
            return true;
          }
        })
      })
    }

    if (targetProp) {
      targetProp.value = payload;
      this.setAllLocations(allLocations);

      this.updatePipe();
    }
  }

  // UPDATE CONTROLLER

  updateControllerIsOnlineStatus(designation: string, isOnline: boolean) {
    const controller = this.getControllerByDesignation(designation);
    const controllerFromFull = this.getControllerByDesignationFromFull(designation)
    if (controller) {
      controller.isOnline = isOnline;
    }

    if (controllerFromFull) {
      controllerFromFull.isOnline = isOnline;
    }
    this.updatePipe();
  }

  updateControllerIsPollingStatus(designation: string, isPolling: boolean) {
    const controller = this.getControllerByDesignation(designation);
    const controllerFromFull = this.getControllerByDesignationFromFull(designation)
    if (controller) {
      controller.isPolling = isPolling;
    }

    if (controllerFromFull) {
      controllerFromFull.isPolling = isPolling;
    }
    this.updatePipe();
  }

  updateIgnoreHvacLogicStatus(designation: string, ignoreHvacLogic: boolean) {
    const controller = this.getControllerByDesignation(designation);
    const controllerFromFull = this.getControllerByDesignationFromFull(designation)
    if (controller) {
      controller.ignoreHvacLogic = ignoreHvacLogic;
    }

    if (controllerFromFull) {
      controllerFromFull.ignoreHvacLogic = ignoreHvacLogic;
    }
    this.updatePipe();
  }

  // UPDATE LOCATION

  updateLocationIsRentedStatus(locationId: string, isRented: boolean) {
    // const location = this.getFilteredLocationById(locationId);
    const onCommonAreasPage = window.document.URL.toString().includes('common-area')
    const onRoomOverviewPage = window.document.URL.toString().includes('rooms')
    let location: Location;
    if (onCommonAreasPage) {
      location = this.getFilteredCommonAreaLocationById(locationId);
    }
    if (onRoomOverviewPage) {
      location = this.getFilteredGuestRoomLocationById(locationId);
    }
    if (location) {
      location.isRented = isRented;
    }

    const locationFromFull = this.getLocationById(locationId)
    if (locationFromFull) {
      locationFromFull.isRented = isRented;
    }
    this.updatePipe();
  }

   updateLocationIsJoinedStatus(locationId: string, isJoined: boolean) {
    let location: Location;
    const onCommonAreasPage = window.document.URL.toString().includes('common-area')
    const onRoomOverviewPage = window.document.URL.toString().includes('rooms')
    if (onCommonAreasPage) {
      location = this.getFilteredCommonAreaLocationById(locationId);
    }
    if (onRoomOverviewPage) {
      location = this.getFilteredGuestRoomLocationById(locationId);
    }

    if (location) {
      location.joinActive = isJoined;
    }
    const locationFromFull = this.getLocationById(locationId)
    if (locationFromFull) {
      locationFromFull.joinActive = isJoined;
    }
    this.updatePipe();
  }

  updateLocationIsOnlineStatus(locationId: string, isOnline: boolean) {
    let location: Location;
    const onCommonAreasPage = window.document.URL.toString().includes('common-area')
    const onRoomOverviewPage = window.document.URL.toString().includes('rooms')
    if (onCommonAreasPage) {
      location = this.getFilteredCommonAreaLocationById(locationId);
    }
    if (onRoomOverviewPage) {
      location = this.getFilteredGuestRoomLocationById(locationId);
    }

    if (location) {
      location.isOnline = isOnline;
    }
    const locationFromFull = this.getLocationById(locationId);
    if (locationFromFull) {
      locationFromFull.isOnline = isOnline;
    }
    this.updatePipe();
  }

  getPropTopic(topic: string): string { // example input: dev/irooms/1/0/0/101/1/hvac/temp/meas/room,
    const topicArray = topic.split('/');
    let propTopic = '';
    for (let index = 7; index < topicArray.length; index++) {
      propTopic = propTopic + topicArray[index];
      if (index < topicArray.length-1) {
        propTopic = propTopic + '/';
      }
    }
    return propTopic;  // example output : hvac/temp/meas/room
  }

  updatePipe() {
    const date = new Date();
    this.rawUpdatedPipe$.next(new Date());
  }


  setNotPolledControllers(controllers: Controller[]) {
    this.notPolledControllers = controllers;
    this.notPolledControllersChanged.next(this.notPolledControllers);
  }

  getNotPolledControllers() {
    return this.notPolledControllers.slice();
  }

  isControllerPolled(controller: Controller) {
    let stopPolling = false;
    const controllers = this.getNotPolledControllers();
    controllers.forEach( cont => {
      if(cont.object === controller.object && cont.subObject === controller.subObject
        && cont.zone === controller.zone && cont.subZone === controller.subZone) {
        stopPolling = true;
      }
    })
    return stopPolling;
  }

  handleCardSyncActive(message: IMqttMessage){
    const value = message.payload.toString().split('|').at(-1);
    if(value === '1'){
      this.cardSyncActive$.next(true);
    }else{
      this.cardSyncActive$.next(false);
    }
  }

  setLatestCardFrom(from: Date){
    const stringFrom = from.toString();
    this.savedDateFromChanged.next(from);
    localStorage.setItem(this.latestCardFromKey, stringFrom);
  }

  getLatestCardFrom(): string{
    const date = localStorage.getItem(this.latestCardFromKey);
    if(date === null || date === undefined){
      return null
    }else{
      return date;
    }
  }

  setLatestCardTo(to: Date){
    const stringTo = to.toString();
    this.savedDateToChanged.next(to);
    localStorage.setItem(this.latestCardToKey, stringTo);
  }

  getLatestCardTo(): string{
    const date = localStorage.getItem(this.latestCardToKey);
    if(date === null || date === undefined){
      return null
    }else{
      return date;
    }
  }

}



